import axios, { AxiosInstance } from 'axios';

export interface ApiResponse<T = any> {
  data: T;
  meta?: {
    total: number;
    page: number;
    limit: number;
  };
}

export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  [key: string]: any;
}

class ApiClient {
  private readonly client: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;

  constructor(baseURL: string = import.meta.env.VITE_API_URL) {
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Skip token refresh for auth endpoints to avoid infinite loops
        if (originalRequest.url?.includes('/auth/')) {
          return Promise.reject(error);
        }
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const newToken = await this.refreshToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.clearToken();
            // Don't redirect here - let React Admin handle it through authProvider
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  private async refreshToken(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = (async () => {
      try {
        const response = await axios.post(`${this.client.defaults.baseURL}/admin/auth/refresh`, {
          refreshToken
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        
        localStorage.setItem('auth_token', accessToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        
        return accessToken;
      } catch (error) {
        this.clearToken();
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private clearToken(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
  }

  // Auth methods
  async login(credentials: { email: string; password: string }) {
    const response = await this.client.post('/admin/auth/login', credentials);
    const { accessToken, refreshToken } = response.data;
    
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    
    return response.data;
  }

  async logout() {
    try {
      await this.client.post('/admin/auth/logout');
    } finally {
      this.clearToken();
    }
  }

  async getMe() {
    const response = await this.client.get('/admin/auth/me');
    return response.data;
  }

  // Generic CRUD methods
  async getList<T = any>(resource: string, params: ListParams = {}): Promise<ApiResponse<T[]>> {
    const { page = 1, limit = 10, search, ...filters } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [key, String(value)])
      ),
    });

    const response = await this.client.get(`/admin/${resource}?${queryParams}`);
    return response.data;
  }

  async getOne<T = any>(resource: string, id: string): Promise<T> {
    const response = await this.client.get(`/admin/${resource}/${id}`);
    return response.data;
  }

  async create<T = any>(resource: string, data: any): Promise<T> {
    const response = await this.client.post(`/admin/${resource}`, data);
    return response.data;
  }

  async update<T = any>(resource: string, id: string, data: any): Promise<T> {
    const response = await this.client.put(`/admin/${resource}/${id}`, data);
    return response.data;
  }

  async delete(resource: string, id: string): Promise<void> {
    await this.client.delete(`/admin/${resource}/${id}`);
  }

  // File upload method for media
  async uploadFile(file: File, metadata?: {
    alt?: string;
    title?: string;
    description?: string;
    categoryId?: string;
    tags?: string[];
  }): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add metadata if provided
    if (metadata) {
      if (metadata.alt) formData.append('alt', metadata.alt);
      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.categoryId) formData.append('categoryId', metadata.categoryId);
      if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));
    }

    const response = await this.client.post('/admin/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  // Multiple file upload method
  async uploadMultipleFiles(files: File[], metadata?: {
    alt?: string;
    title?: string;
    description?: string;
    categoryId?: string;
    tags?: string[];
  }): Promise<{ success: any[]; errors: any[] }> {
    const results: any[] = [];
    const errors: any[] = [];

    for (const file of files) {
      try {
        const result = await this.uploadFile(file, metadata);
        results.push(result);
      } catch (error: any) {
        errors.push({ file: file.name, error: error.message });
      }
    }

    return { success: results, errors };
  }

  // Media file methods
  async getMediaFiles(params: ListParams = {}): Promise<ApiResponse> {
    return this.getList('media', params);
  }

  async deleteMediaFile(id: string): Promise<void> {
    await this.delete('media', id);
  }

  // Bulk delete media files
  async bulkDeleteMediaFiles(ids: string[]): Promise<{ deleted: number; failed: Array<{ id: string; error: string }> }> {
    const response = await this.client.delete('/admin/media/bulk', {
      data: { ids }
    });
    return response.data;
  }
}

export const apiClient = new ApiClient();
